import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** @param {string} s */
export function parseMetricValue(s) {
  if (s == null || typeof s !== 'string') return { type: 'static', text: String(s) };
  const t = s.trim();
  const ratio = t.match(/^(\d+(?:\.\d+)?)\/(.+)$/);
  if (ratio) {
    const target = parseFloat(ratio[1]);
    if (!Number.isNaN(target)) {
      return { type: 'ratio', target, tail: `/${ratio[2]}` };
    }
  }
  const intPlus = t.match(/^([\d,]+)\+$/);
  if (intPlus) {
    const n = parseInt(intPlus[1].replace(/,/g, ''), 10);
    if (!Number.isNaN(n)) return { type: 'intPlus', target: n, suffix: '+' };
  }
  return { type: 'static', text: t };
}

function easeOutQuart(t) {
  return 1 - (1 - t) ** 4;
}

function formatInt(n) {
  return Math.max(0, Math.round(n)).toLocaleString('en-IN');
}

function computeIntStart(target, startFrom, startRatio) {
  if (startFrom != null && Number.isFinite(startFrom)) {
    const v = Math.floor(startFrom);
    return Math.max(0, Math.min(v, Math.max(0, target - 1)));
  }
  const ratio = startRatio ?? 0.14;
  let base = Math.floor(target * ratio);
  if (target >= 3 && base < 1) base = 1;
  if (base >= target) base = Math.max(0, target - 1);
  return base;
}

function computeRatioStart(target, startFrom, startRatio) {
  if (startFrom != null && Number.isFinite(startFrom)) {
    return Math.max(0, Math.min(startFrom, target - 0.05));
  }
  const ratio = startRatio ?? 0.58;
  return Math.min(target - 0.05, Math.max(0, target * ratio));
}

function startTextForParsed(parsed, fromInt, fromRatio) {
  if (parsed.type === 'intPlus') {
    return `${formatInt(fromInt)}${parsed.suffix}`;
  }
  if (parsed.type === 'ratio') {
    return `${fromRatio.toFixed(1)}${parsed.tail}`;
  }
  return parsed.text;
}

function finalText(parsed) {
  if (parsed.type === 'intPlus') {
    return `${formatInt(parsed.target)}${parsed.suffix}`;
  }
  if (parsed.type === 'ratio') {
    return `${parsed.target.toFixed(1)}${parsed.tail}`;
  }
  return parsed.text;
}

/**
 * Count-up when scrolled into view (Hostinger-safe: client-only).
 * @param {{
 *   value: string,
 *   className?: string,
 *   duration?: number,
 *   delay?: number,
 *   trigger?: 'intersection' | 'mount',
 *   startFrom?: number,
 *   startRatio?: number,
 *   intersectionThreshold?: number,
 *   intersectionRootMargin?: string,
 * }} props
 */
export default function AnimatedMetric({
  value,
  className = '',
  duration = 2200,
  delay = 0,
  trigger = 'intersection',
  startFrom,
  startRatio,
  /** If > 0, require this intersectionRatio;0 = rely on rootMargin only (better for small text on mobile). */
  intersectionThreshold = 0,
  /** Shrinks the “viewable” root from the bottom so counts don’t run while still in the lower strip. */
  intersectionRootMargin = '0px 0px -16% 0px',
}) {
  const ref = useRef(null);
  const parsed = useMemo(() => parseMetricValue(value), [value]);

  const fromValues = useMemo(() => {
    if (parsed.type === 'intPlus') {
      const from = computeIntStart(parsed.target, startFrom, startRatio);
      return { fromInt: from, fromRatio: 0 };
    }
    if (parsed.type === 'ratio') {
      const from = computeRatioStart(parsed.target, startFrom, startRatio);
      return { fromInt: 0, fromRatio: from };
    }
    return { fromInt: 0, fromRatio: 0 };
  }, [parsed, startFrom, startRatio]);

  const [text, setText] = useState(() => {
    if (prefersReducedMotion() || parsed.type === 'static') return value;
    if (parsed.type === 'intPlus') {
      return startTextForParsed(parsed, fromValues.fromInt, fromValues.fromRatio);
    }
    if (parsed.type === 'ratio') {
      return startTextForParsed(parsed, fromValues.fromInt, fromValues.fromRatio);
    }
    return value;
  });

  const rafRef = useRef(0);

  const animate = useCallback(() => {
    if (prefersReducedMotion() || parsed.type === 'static') {
      setText(value);
      return;
    }

    const fromInt = fromValues.fromInt;
    const fromRat = fromValues.fromRatio;

    if (parsed.type === 'intPlus') {
      setText(startTextForParsed(parsed, fromInt, 0));
    } else if (parsed.type === 'ratio') {
      setText(startTextForParsed(parsed, 0, fromRat));
    }

    const startAt = performance.now() + delay;
    const dur = Math.max(400, duration);

    const tick = (now) => {
      if (now < startAt) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = now - startAt;
      const t = Math.min(1, elapsed / dur);
      const eased = easeOutQuart(t);

      if (parsed.type === 'intPlus') {
        const current = fromInt + (parsed.target - fromInt) * eased;
        setText(`${formatInt(current)}${parsed.suffix}`);
      } else if (parsed.type === 'ratio') {
        const current = fromRat + (parsed.target - fromRat) * eased;
        setText(`${current.toFixed(1)}${parsed.tail}`);
      }

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setText(finalText(parsed));
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [value, parsed, duration, delay, fromValues]);

  useEffect(() => {
    if (prefersReducedMotion() || parsed.type === 'static') {
      setText(value);
      return undefined;
    }

    setText(startTextForParsed(parsed, fromValues.fromInt, fromValues.fromRatio));

    let observer;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      cancelAnimationFrame(rafRef.current);
      animate();
    };

    if (trigger === 'mount') {
      start();
      return () => {
        cancelled = true;
        cancelAnimationFrame(rafRef.current);
      };
    }

    const el = ref.current;
    if (!el) return undefined;

    observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        if (intersectionThreshold > 0 && e.intersectionRatio < intersectionThreshold) return;
        observer.disconnect();
        start();
      },
      {
        threshold: intersectionThreshold > 0 ? [0, 0.1, intersectionThreshold, 0.5, 1] : [0],
        rootMargin: intersectionRootMargin,
      }
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      observer?.disconnect();
    };
  }, [
    value,
    parsed,
    animate,
    trigger,
    fromValues.fromInt,
    fromValues.fromRatio,
    intersectionThreshold,
    intersectionRootMargin,
  ]);

  return (
    <span ref={ref} className={`tabular-nums ${className}`.trim()}>
      {text}
    </span>
  );
}
